import type {
  FinOpsContext,
  FinOpsContextConfig,
  BudgetState,
  CostAlertPayload,
  CostUpdatePayload,
} from './types';
import { BudgetManager, createBudgetManager } from './budget';

/**
 * FinOps context implementation with multi-session support.
 * 
 * Provides cost tracking across multiple sessions with a unified interface
 * for adapters to integrate with budget management.
 * 
 * @example
 * ```typescript
 * const context = createFinOpsContext({
 *   budgetConfig: { usd: 10, perRequestCents: 50 },
 *   debug: true,
 * });
 * 
 * // Track cost for a session
 * await context.trackCost({
 *   sessionId: 'user-123',
 *   tokens: 1000,
 *   costPerMTokenUsd: 0.002,
 *   model: 'gpt-4o-mini',
 * });
 * 
 * // Check budget state
 * const state = await context.getState('user-123');
 * console.log(`Spent: $${state.totalUsd.toFixed(4)}`);
 * ```
 */
export class FinOpsContextImpl implements FinOpsContext {
  private sessions: Map<string, BudgetManager> = new Map();
  private defaultSession: BudgetManager;
  private costAlertHandlers: Array<(payload: CostAlertPayload) => void | Promise<void>> = [];
  private costUpdateHandlers: Array<(payload: CostUpdatePayload) => void | Promise<void>> = [];
  private debug: boolean;

  constructor(config: FinOpsContextConfig = {}) {
    this.debug = config.debug ?? false;

    // Create default budget manager
    if (config.budgetManager) {
      this.defaultSession = config.budgetManager;
    } else {
      this.defaultSession = createBudgetManager(config.budgetConfig ?? {});
    }

    // Wire up default session event handlers
    this.setupEventHandlers(this.defaultSession);

    if (this.debug) {
      console.log('[FinOpsContext] Initialized with config:', {
        hasBudgetManager: !!config.budgetManager,
        budgetConfig: config.budgetConfig,
      });
    }
  }

  /**
   * Track cost for a request
   */
  async trackCost(options: {
    sessionId?: string;
    tokens: number;
    costPerMTokenUsd: number;
    model?: string;
  }): Promise<BudgetState> {
    const manager = this.getOrCreateSession(options.sessionId);

    if (this.debug) {
      console.log('[FinOpsContext] Tracking cost:', {
        sessionId: options.sessionId ?? 'default',
        tokens: options.tokens,
        model: options.model,
      });
    }

    const state = manager.recordUsage(options.tokens, options.costPerMTokenUsd, options.model);
    return state;
  }

  /**
   * Check if a request would exceed the budget
   */
  async wouldExceed(options: {
    sessionId?: string;
    estimatedTokens: number;
    costPerMTokenUsd: number;
  }): Promise<boolean> {
    const manager = this.getOrCreateSession(options.sessionId);
    return manager.wouldExceed(options.estimatedTokens, options.costPerMTokenUsd);
  }

  /**
   * Get budget state for a session
   */
  async getState(sessionId?: string): Promise<BudgetState> {
    const manager = this.getOrCreateSession(sessionId);
    return manager.getState();
  }

  /**
   * Reset budget for a session
   */
  async reset(sessionId?: string): Promise<void> {
    const manager = this.getOrCreateSession(sessionId);
    manager.reset();

    if (this.debug) {
      console.log('[FinOpsContext] Budget reset for session:', sessionId ?? 'default');
    }
  }

  /**
   * Register a cost alert handler
   */
  onCostAlert(handler: (payload: CostAlertPayload) => void | Promise<void>): void {
    this.costAlertHandlers.push(handler);
  }

  /**
   * Register a cost update handler
   */
  onCostUpdate(handler: (payload: CostUpdatePayload) => void | Promise<void>): void {
    this.costUpdateHandlers.push(handler);
  }

  /**
   * Get all active sessions
   */
  async getSessions(): Promise<string[]> {
    return Array.from(this.sessions.keys());
  }

  /**
   * Get or create a budget manager for a session
   */
  private getOrCreateSession(sessionId?: string): BudgetManager {
    if (!sessionId) {
      return this.defaultSession;
    }

    let manager = this.sessions.get(sessionId);
    if (!manager) {
      // Create new session with same config as default
      const config = this.defaultSession.getConfig();
      manager = createBudgetManager(config);
      this.setupEventHandlers(manager, sessionId);
      this.sessions.set(sessionId, manager);

      if (this.debug) {
        console.log('[FinOpsContext] Created new session:', sessionId);
      }
    }

    return manager;
  }

  /**
   * Setup event handlers for a budget manager
   */
  private setupEventHandlers(manager: BudgetManager, sessionId?: string): void {
    manager.onEvent((event) => {
      if (event.type === 'cost:alert') {
        const payload = event.payload as CostAlertPayload;
        const enrichedPayload = { ...payload, sessionId };
        this.costAlertHandlers.forEach((handler) => {
          try {
            const result = handler(enrichedPayload);
            if (result instanceof Promise) {
              result.catch((error) => {
                console.error('[FinOpsContext] Cost alert handler error:', error);
              });
            }
          } catch (error) {
            console.error('[FinOpsContext] Cost alert handler error:', error);
          }
        });
      } else if (event.type === 'cost:update') {
        const payload = event.payload as CostUpdatePayload;
        const enrichedPayload = { ...payload, sessionId };
        this.costUpdateHandlers.forEach((handler) => {
          try {
            const result = handler(enrichedPayload);
            if (result instanceof Promise) {
              result.catch((error) => {
                console.error('[FinOpsContext] Cost update handler error:', error);
              });
            }
          } catch (error) {
            console.error('[FinOpsContext] Cost update handler error:', error);
          }
        });
      }
    });
  }
}

/**
 * Create a FinOps context for cost tracking and budget management.
 * 
 * @param config - FinOps context configuration
 * @returns FinOps context instance
 * 
 * @example
 * ```typescript
 * // With budget config
 * const context = createFinOpsContext({
 *   budgetConfig: {
 *     usd: 10,
 *     perRequestCents: 50,
 *     onExceed: 'halt',
 *   },
 *   debug: true,
 * });
 * 
 * // With existing budget manager
 * const manager = createBudgetManager({ usd: 5 });
 * const context = createFinOpsContext({
 *   budgetManager: manager,
 * });
 * 
 * // Track cost
 * await context.trackCost({
 *   sessionId: 'session-123',
 *   tokens: 1000,
 *   costPerMTokenUsd: 0.002,
 *   model: 'gpt-4o-mini',
 * });
 * ```
 */
export function createFinOpsContext(config: FinOpsContextConfig = {}): FinOpsContext {
  return new FinOpsContextImpl(config);
}

